import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ProductsService, auth } from '../firebase';
import { Product } from '../types/product';
import { ShoppingCart, Heart, Share2, ArrowLeft, X, Calendar } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { setPageMeta } from '../components/PageMeta';

const TRANSPARENT_PIXEL =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==';

const PageContainer = styled.div`
  padding-top: var(--header-h);
  min-height: 100vh;
  background: white;
  width: 100%;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 40px;
  display: grid;
  grid-template-columns: 120px 1fr 1fr;
  gap: 40px;
  min-height: calc(100vh - var(--header-h));
  
  @media (max-width: 1024px) {
    grid-template-columns: 80px 1fr 1fr;
    gap: 20px;
    padding: 20px;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
    padding: 16px;
    min-height: auto;
  }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: white;
  border: 1px solid #000;
  color: #000;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  cursor: pointer;
  padding: 9px 16px;
  border-radius: 2px;
  transition: all 0.25s ease;
  position: fixed;
  /* Header yuksekligi degiskenden geliyor; buton her zaman onun altinda kalir */
  top: calc(var(--header-h) + 20px);
  left: 40px;
  z-index: 1000;

  @media (max-width: 768px) {
    top: calc(var(--header-h) + 10px);
    left: 12px;
    font-size: 10px;
    padding: 6px 10px;
  }

  &:hover {
    background: #000;
    color: white;
  }
`;

const ThumbnailColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  height: 100%;
  overflow-y: auto;
  padding-right: 10px;
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 2px;
  }
  
  @media (max-width: 768px) {
    flex-direction: row;
    overflow-x: auto;
    overflow-y: hidden;
    width: 100%;
    padding-bottom: 10px;
    
    &::-webkit-scrollbar {
      height: 4px;
    }
  }
`;

const ImageSection = styled.div`
  position: sticky;
  top: 140px;
  height: calc(100vh - 160px);
  overflow: hidden;
  
  @media (max-width: 1024px) {
    top: var(--header-h);
    height: calc(100vh - 140px);
  }
  
  @media (max-width: 768px) {
    position: relative;
    top: 0;
    height: 70vh;
  }
`;

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: 15px;
`;

const MainImage = styled.img<{ $isActive: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  image-rendering: -webkit-optimize-contrast;
  image-rendering: auto;
  -ms-interpolation-mode: bicubic;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  opacity: ${props => props.$isActive ? 1 : 0};
  transition: opacity 0.8s ease-in-out, transform 0.8s ease-in-out;
  transform: translateY(${props => props.$isActive ? '0' : '20px'}) translateZ(0);
  border-radius: 15px;
  cursor: pointer;
  will-change: opacity, transform;
`;

const ImageBadges = styled.div`
  position: absolute;
  top: 20px;
  left: 20px;
  z-index: 10;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Badge = styled.span<{ type: 'sale' | 'featured' | 'new' | 'soldout' }>`
  padding: 7px 14px;
  border-radius: 2px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  line-height: 1;
  backdrop-filter: blur(6px);

  ${props => {
    switch (props.type) {
      case 'sale':
        return 'background: rgba(255, 255, 255, 0.94); color: #b3242f; border: 1px solid rgba(179, 36, 47, 0.35);';
      case 'featured':
        return 'background: #1a1a1a; color: #ffffff; border: 1px solid #1a1a1a;';
      case 'new':
        return 'background: rgba(255, 255, 255, 0.94); color: #1a1a1a; border: 1px solid rgba(26, 26, 26, 0.25);';
      case 'soldout':
        return 'background: rgba(26, 26, 26, 0.82); color: #ffffff; border: 1px solid rgba(26, 26, 26, 0.82);';
      default:
        return 'background: rgba(255, 255, 255, 0.94); color: #1a1a1a; border: 1px solid rgba(26, 26, 26, 0.15);';
    }
  }}
`;

const ThumbnailImage = styled.img<{ $isActive?: boolean }>`
  width: 100px;
  height: 100px;
  object-fit: cover;
  object-position: center;
  image-rendering: -webkit-optimize-contrast;
  image-rendering: auto;
  -ms-interpolation-mode: bicubic;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  transform: translateZ(0);
  border-radius: 8px;
  cursor: pointer;
  flex-shrink: 0;
  border: 2px solid ${props => props.$isActive ? '#1a1a1a' : 'transparent'};
  transition: border-color 0.3s ease;
  
  @media (max-width: 1024px) {
    width: 70px;
    height: 70px;
  }
  
  @media (max-width: 768px) {
    width: 80px;
    height: 80px;
  }
`;

const ProductInfo = styled.div`
  padding: 20px 0;
`;

const ProductTitle = styled.h1`
  font-size: 2.5rem;
  color: #2c2c2c;
  margin-bottom: 15px;
  font-weight: 300;
  line-height: 1.2;
`;

const ProductPrice = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 25px;
`;

const CurrentPrice = styled.span<{ $onSale?: boolean }>`
  font-size: 2rem;
  font-weight: 600;
  color: ${props => props.$onSale ? '#ff4757' : '#1a1a1a'};
`;

const OriginalPrice = styled.span`
  font-size: 1.5rem;
  color: #999;
  text-decoration: line-through;
`;

const DiscountPercent = styled.span`
  background: #ff4757;
  color: white;
  padding: 5px 12px;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
`;

const SizeSelector = styled.div`
  margin-bottom: 30px;
`;

const SizeLabel = styled.h3`
  font-size: 1.2rem;
  color: #333;
  margin-bottom: 15px;
  font-weight: 600;
`;

const SizeOptions = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const SizeOption = styled.button<{ $isSelected?: boolean }>`
  padding: 12px 20px;
  border: 2px solid ${props => props.$isSelected ? '#1a1a1a' : '#ddd'};
  background: ${props => props.$isSelected ? '#1a1a1a' : 'white'};
  color: ${props => props.$isSelected ? 'white' : '#666'};
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.3s ease;

  &:hover {
    border-color: #1a1a1a;
    background: #1a1a1a;
    color: white;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 15px;
  margin-bottom: 30px;
`;

const AddToCartButton = styled.button`
  flex: 1;
  padding: 18px 30px;
  background: #000;
  color: white;
  border: 2px solid #000;
  border-radius: 4px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: all 0.3s ease;

  &:hover {
    background: white;
    color: #000;
  }
  &:disabled {
    background: #cfcfcf;
    color: #7a7a7a;
    cursor: not-allowed;
    transform: none;
  }
`;

const WishlistButton = styled.button`
  padding: 18px;
  background: white;
  border: 2px solid #000;
  color: #000;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &:hover {
    background: #000;
    color: white;
  }
`;

const ShareButton = styled.button`
  padding: 18px;
  background: white;
  border: 2px solid #000;
  color: #000;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &:hover {
    background: #000;
    color: white;
  }
`;

// --- Randevu butonu (Sepete Ekle'nin hemen altinda) ---
const AppointmentButton = styled.button`
  width: 100%;
  padding: 15px 24px;
  margin-top: 12px;
  background: white;
  color: #1a1a1a;
  border: 1px solid #1a1a1a;
  border-radius: 2px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: all 0.25s ease;

  &:hover {
    background: #1a1a1a;
    color: white;
  }
`;

// --- Gorsellerin altindaki tam genislikte bilgi bolumu ---
const InfoSection = styled.section`
  max-width: 1100px;
  margin: 80px auto 100px;
  padding: 0 40px;

  @media (max-width: 768px) {
    margin: 50px auto 60px;
    padding: 0 20px;
  }
`;

const InfoSectionTitle = styled.h2`
  font-size: 22px;
  font-weight: 400;
  color: #1a1a1a;
  letter-spacing: 1px;
  margin: 0 0 8px 0;
`;

const InfoSectionRule = styled.div`
  width: 50px;
  height: 1px;
  background: #1a1a1a;
  opacity: 0.35;
  margin-bottom: 32px;
`;

const InfoDescription = styled.p`
  font-size: 15px;
  line-height: 1.85;
  color: #444;
  margin: 0 0 36px 0;
  white-space: pre-line;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 0 40px;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 20px;
  padding: 13px 0;
  border-bottom: 1px solid #eee;
  font-size: 14px;
`;

const InfoLabel = styled.span`
  color: #777;
  flex-shrink: 0;
`;

const InfoValue = styled.span`
  color: #1a1a1a;
  text-align: right;
  font-weight: 500;
`;

const LoadingState = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.95);
  z-index: 9999;
  
  p {
    font-size: 1.1rem;
    color: #666;
    margin-top: 20px;
  }
`;

const LoadingIcon = styled.img`
  width: 150px;
  height: 150px;
`;

const ErrorState = styled.div`
  text-align: center;
  padding: 80px 20px;
  
  h2 {
    font-size: 2rem;
    color: #1a1a1a;
    margin-bottom: 20px;
  }
  
  p {
    color: #666;
    font-size: 1.1rem;
  }
`;

const CinemaModal = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 10000;
  cursor: pointer;
`;

const CinemaImageWrapper = styled.div`
  position: relative;
  width: 90vw;
  height: 90vh;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CinemaImage = styled.img<{ $isZoomed?: boolean; $translateX?: number; $translateY?: number }>`
  width: 90vw;
  height: 90vh;
  object-fit: contain;
  object-position: center;
  image-rendering: -webkit-optimize-contrast;
  image-rendering: auto;
  -ms-interpolation-mode: bicubic;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  cursor: ${props => props.$isZoomed ? 'grab' : 'zoom-in'};
  transform: translate(${props => props.$translateX || 0}px, ${props => props.$translateY || 0}px) scale(${props => props.$isZoomed ? '2' : '1'}) translateZ(0);
  transition: ${props => props.$isZoomed ? 'none' : 'transform 0.3s ease'};
  will-change: transform;
  
  &:active {
    cursor: ${props => props.$isZoomed ? 'grabbing' : 'zoom-in'};
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10001;
  transition: all 0.3s ease;
  
  &:hover {
    background: white;
    transform: scale(1.1);
  }
  
  svg {
    color: #000;
  }
`;

const ProductDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedSize, setSelectedSize] = useState<string | null>(null);
  const [isCinemaMode, setIsCinemaMode] = useState(false);
  const [isZoomed, setIsZoomed] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [translatePos, setTranslatePos] = useState({ x: 0, y: 0 });
  const [lastTranslatePos, setLastTranslatePos] = useState({ x: 0, y: 0 });
  const [imageRetries, setImageRetries] = useState<Map<number, number>>(new Map());
  const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set([0]));
  const [thumbnailQueue, setThumbnailQueue] = useState<Set<number>>(new Set());
  const thumbnailObserverRef = useRef<IntersectionObserver | null>(null);
  const thumbnailColumnRef = useRef<HTMLDivElement | null>(null);

  const categoryMap: Record<string, string> = {
    'gelinlik': '/gelinlikler',
    'abiye': '/abiyeler',
    'kinalik': '/kinaliklar',
    'aksesuar': '/aksesuar'
  };

  const loadProduct = async () => {
    if (!slug) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Decode the URL slug to get the product name
      const productName = decodeURIComponent(slug);
      const result = await ProductsService.getProductByName(productName);
      if (result.success && result.data) {
        setProduct(result.data);
      } else {
        setError(result.error || 'Ürün bulunamadı');
      }
    } catch (err) {
      setError('Ürün yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (slug) {
      loadProduct();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isCinemaMode || !product) return;

      if (e.key === 'Escape') {
        setIsCinemaMode(false);
        setIsZoomed(false);
        setTranslatePos({ x: 0, y: 0 });
        setLastTranslatePos({ x: 0, y: 0 });
      } else if (e.key === 'ArrowLeft') {
        setSelectedImage((prev) => (prev > 0 ? prev - 1 : product.images.length - 1));
        setIsZoomed(false);
        setTranslatePos({ x: 0, y: 0 });
        setLastTranslatePos({ x: 0, y: 0 });
      } else if (e.key === 'ArrowRight') {
        setSelectedImage((prev) => (prev < product.images.length - 1 ? prev + 1 : 0));
        setIsZoomed(false);
        setTranslatePos({ x: 0, y: 0 });
        setLastTranslatePos({ x: 0, y: 0 });
      }
    };

    if (isCinemaMode) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleKeyDown);
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isCinemaMode, product]);

  // Urun yuklendiginde sayfa basligini ve aciklamasini ayarla
  useEffect(() => {
    if (!product) return;
    setPageMeta(
      `${product.name} | Saraylı Moda`,
      product.shortDescription || product.detailedDescription || ''
    );
  }, [product]);

  const handleAddToCart = () => {
    if (!product) return;

    if (product.stock === 0) {
      alert('Bu ürün şu anda stokta bulunmuyor.');
      return;
    }

    if (!selectedSize) {
      alert('Lütfen bir beden seçiniz');
      return;
    }
    
    addToCart({
      id: product.id || '',
      name: product.name,
      description: product.shortDescription,
      image: product.images[0] || '/placeholder.webp',
      price: product.price,
      size: selectedSize,
      slug: product.slug,
      maxStock: product.stock
    });
    
    alert('Ürün sepete eklendi!');
  };

  const handleWishlist = () => {
    if (!product) return;

    // Favorileme giris gerektirir: favoriler kullaniciya ait bir veri,
    // oturum yoksa kime ait olacagi belirsiz.
    if (!auth.currentUser) {
      alert('Ürünü favorilerinize eklemek için giriş yapmalısınız.');
      navigate('/login');
      return;
    }

    console.log('Favorilere eklendi:', product.name);
    alert('Ürün favorilere eklendi!');
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: product?.name,
        text: product?.shortDescription,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link kopyalandı!');
    }
  };

  const handleZoomToggle = () => {
    if (!isZoomed) {
      // Zoom in
      setIsZoomed(true);
      setTranslatePos({ x: 0, y: 0 });
      setLastTranslatePos({ x: 0, y: 0 });
    } else {
      // Zoom out
      setIsZoomed(false);
      setTranslatePos({ x: 0, y: 0 });
      setLastTranslatePos({ x: 0, y: 0 });
    }
  };

  const handleImageMouseDown = (e: React.MouseEvent) => {
    if (isZoomed) {
      // Start dragging when zoomed
      setIsDragging(true);
      setDragStart({ x: e.clientX, y: e.clientY });
    }
  };

  const handleImageMouseMove = (e: React.MouseEvent) => {
    if (isDragging && isZoomed) {
      const deltaX = e.clientX - dragStart.x;
      const deltaY = e.clientY - dragStart.y;
      setTranslatePos({
        x: lastTranslatePos.x + deltaX,
        y: lastTranslatePos.y + deltaY
      });
    }
  };

  const handleImageMouseUp = () => {
    if (isDragging) {
      setIsDragging(false);
      setLastTranslatePos(translatePos);
    }
  };

  const handleImageClick = (e: React.MouseEvent) => {
    if (!isDragging && !isZoomed) {
      // Only zoom in if not dragging
      handleZoomToggle();
      e.stopPropagation();
    } else if (!isDragging) {
      // Zoom out if zoomed and not dragging
      handleZoomToggle();
      e.stopPropagation();
    }
  };

  const handleImageError = (index: number, event?: React.SyntheticEvent<HTMLImageElement, Event>) => {
    const img = event?.currentTarget;
    if (!img || !product) return;
    
    const retryCount = imageRetries.get(index) || 0;
    const maxRetries = 5; // 5 farklı URL formatı deneyebiliriz
    
    // 403 veya 429 hatası için retry mekanizması
    if (retryCount < maxRetries) {
      const newRetryCount = retryCount + 1;
      setImageRetries(prev => new Map(prev).set(index, newRetryCount));
      
      // Exponential backoff: 0.5s, 1s, 2s, 4s, 8s
      const delay = Math.pow(2, retryCount) * 500;
      
      setTimeout(() => {
        // Farklı URL formatını dene
        const originalUrl = product.images[index];
        const newUrl = getImageUrl(originalUrl, newRetryCount);
        
        // Yeni URL ile tekrar dene
        img.src = newUrl;
      }, delay);
    } else {
      // Max retry sayısına ulaşıldı
      console.warn(`Image ${index} failed to load after ${maxRetries} retries. Please check Google Drive sharing settings.`);
    }
  };

  const extractGoogleDriveFileId = (url: string): string | null => {
    // Farklı Google Drive URL formatlarından FILE_ID çıkar
    const patterns = [
      /googleusercontent\.com\/d\/([a-zA-Z0-9_-]+)/,
      /drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/,
      /drive\.google\.com\/.*[?&]id=([a-zA-Z0-9_-]+)/,
      /thumbnail\?id=([a-zA-Z0-9_-]+)/,
      /uc\?export=view&id=([a-zA-Z0-9_-]+)/
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }
    
    return null;
  };

  const getGoogleDriveUrlVariants = (fileId: string): string[] => {
    // Farklı Google Drive URL formatlarını dene
    // Sıralama: En güvenilir formatlardan en az güvenilir olanlara
    return [
      `https://drive.google.com/thumbnail?id=${fileId}&sz=w1920`, // Thumbnail format (genellikle en güvenilir)
      `https://drive.google.com/file/d/${fileId}/preview`, // Preview format (genellikle çalışır)
      `https://drive.google.com/uc?export=view&id=${fileId}`, // Export view format
      `https://drive.google.com/file/d/${fileId}/view`, // View format
      `https://lh3.googleusercontent.com/d/${fileId}=w1920` // Googleusercontent format (son çare)
    ];
  };

  const getImageUrl = (url: string, retryIndex: number = 0): string => {
    // If it's an Unsplash URL, try to add CORS-friendly parameters
    if (url.includes('images.unsplash.com')) {
      // Remove existing query params and add CORS-friendly ones
      const baseUrl = url.split('?')[0];
      return `${baseUrl}?auto=format&fit=crop&w=1920&q=80`;
    }
    
    // Google Drive URL'lerini optimize et
    if (url.includes('googleusercontent.com') || url.includes('drive.google.com')) {
      const fileId = extractGoogleDriveFileId(url);
      
      if (fileId) {
        // Farklı URL formatlarını dene (retry index'e göre)
        const variants = getGoogleDriveUrlVariants(fileId);
        const variantIndex = Math.min(retryIndex, variants.length - 1);
        return variants[variantIndex];
      }
    }
    
    return url;
  };

  const shouldUseCrossOrigin = (url: string): boolean => {
    if (!url) {
      return false;
    }

    const normalizedUrl = url.toLowerCase();
    const corsSafeHosts = [
      'firebasestorage.googleapis.com',
      'storage.googleapis.com',
      'lh3.googleusercontent.com'
    ];

    return corsSafeHosts.some(host => normalizedUrl.includes(host));
  };

  useEffect(() => {
    setLoadedImages(new Set([0]));
    const initialThumbnailSet = new Set<number>();
    const imagesLength = product?.images?.length || 0;
    const initialCount = Math.min(4, imagesLength);
    for (let i = 0; i < initialCount; i += 1) {
      initialThumbnailSet.add(i);
    }
    setThumbnailQueue(initialThumbnailSet);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id]);

  useEffect(() => {
    setLoadedImages(prev => {
      if (prev.has(selectedImage)) {
        return prev;
      }

      const next = new Set(prev);
      next.add(selectedImage);
      return next;
    });
  }, [selectedImage]);

  useEffect(() => {
    if (thumbnailObserverRef.current) {
      thumbnailObserverRef.current.disconnect();
    }

    thumbnailObserverRef.current = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const indexAttr = entry.target.getAttribute('data-index');
            if (indexAttr) {
              const index = parseInt(indexAttr, 10);
              setThumbnailQueue(prev => {
                if (prev.has(index)) {
                  return prev;
                }
                const next = new Set(prev);
                next.add(index);
                return next;
              });
            }
            thumbnailObserverRef.current?.unobserve(entry.target);
          }
        });
      },
      {
        root: thumbnailColumnRef.current,
        rootMargin: '100px',
        threshold: 0.1
      }
    );

    return () => {
      thumbnailObserverRef.current?.disconnect();
    };
  }, [product?.id]);

  const registerThumbnailRef = useCallback(
    (index: number) => (element: HTMLImageElement | null) => {
      if (!element) {
        return;
      }

      element.setAttribute('data-index', index.toString());

      if (thumbnailQueue.has(index)) {
        return;
      }

      thumbnailObserverRef.current?.observe(element);
    },
    [thumbnailQueue]
  );

  if (loading) {
    return (
      <PageContainer>
        <LoadingState>
          <LoadingIcon src="/wedding-dress.gif" alt="Loading..." />
          <p>Ürün yükleniyor...</p>
        </LoadingState>
      </PageContainer>
    );
  }

  if (error || !product) {
    return (
      <PageContainer>
        <Container>
          <BackButton onClick={() => {
            if (product) {
              navigate(categoryMap[product.category] || '/');
            } else {
              navigate(-1);
            }
          }}>
            <ArrowLeft size={20} />
            Geri Dön
          </BackButton>
          <ErrorState>
            <h2>Ürün Bulunamadı</h2>
            <p>{error || 'Aradığınız ürün bulunamadı.'}</p>
          </ErrorState>
        </Container>

      </PageContainer>
    );
  }

  // --- Asagidaki "Urun Bilgileri" bolumu ---
  // Yonetim panelinde urun eklenirken sorulan alanlardan yalnizca DOLU olanlar
  // listelenir; bos alanlar hic gosterilmez.
  const categoryTitles: Record<string, string> = {
    gelinlik: 'Gelinlik Bilgileri',
    abiye: 'Abiye Bilgileri',
    kinalik: 'Kınalık Bilgileri',
    aksesuar: 'Ürün Bilgileri',
  };
  const infoSectionTitle = categoryTitles[product.category] || 'Ürün Bilgileri';

  const infoRows: Array<{ label: string; value: string }> = [];
  const addInfo = (
    label: string,
    value?: string | number | boolean | string[] | null
  ) => {
    if (value === undefined || value === null) return;

    let text: string;
    if (Array.isArray(value)) {
      const cleaned = value.filter(Boolean);
      if (cleaned.length === 0) return;
      text = cleaned.join(', ');
    } else if (typeof value === 'boolean') {
      text = value ? 'Evet' : 'Hayır';
    } else {
      text = String(value).trim();
    }

    if (!text) return;
    infoRows.push({ label, value: text });
  };

  addInfo('Kategori', product.category);
  addInfo('Alt Kategori', product.subcategory);
  addInfo('Renkler', product.colors);
  addInfo('Malzemeler', product.materials);
  addInfo('Özellikler', product.features);
  addInfo('Bedenler', product.sizes?.map((sz) => sz.size));
  addInfo('Stok', typeof product.stock === 'number' ? `${product.stock} adet` : undefined);

  const wd = product.weddingDressDetails;
  if (wd) {
    addInfo('Model Tipi', wd.modelType);
    addInfo('Yaka Tipi', wd.necklineType);
    addInfo('Duvak Uyumu', wd.veilCompatible);
    addInfo('Sırt Detayı', wd.backStyle);
    addInfo('Kuyruk Uzunluğu', wd.trainLength);
  }

  const ed = product.eveningDressDetails;
  if (ed) {
    addInfo('Parıltı Seviyesi', ed.sparkleLevel);
    addInfo('Etkinlik Türü', ed.eventType);
    addInfo('Askı Tipi', ed.strapType);
  }

  const hd = product.hennaDressDetails;
  if (hd) {
    addInfo('İşleme Türü', hd.embroideryType);
    addInfo('Aksesuar Seti', hd.accessorySet);
    addInfo('Renk Teması', hd.colorTheme);
    addInfo('Kaftan Uzunluğu', hd.kaftanLength);
  }

  addInfo('Etiketler', product.tags);

  return (
    <PageContainer>
      <BackButton onClick={() => {
        if (product) {
          navigate(categoryMap[product.category] || '/');
        } else {
          navigate(-1);
        }
      }}>
        <ArrowLeft size={20} />
        Geri Dön
      </BackButton>
      
      <Container>
        <ThumbnailColumn ref={thumbnailColumnRef}>
          {product.images.map((image, index) => (
            <ThumbnailImage
              key={index}
              ref={registerThumbnailRef(index)}
              src={thumbnailQueue.has(index) ? getImageUrl(image, 0) : TRANSPARENT_PIXEL}
              alt={`${product.name} - Görsel ${index + 1}`}
              $isActive={index === selectedImage}
              sizes="100px"
              onClick={() => setSelectedImage(index)}
              onError={(e) => handleImageError(index, e)}
              {...(shouldUseCrossOrigin(image) ? { crossOrigin: "anonymous" } : {})}
              loading="lazy"
              decoding="async"
            />
          ))}
        </ThumbnailColumn>

        <ImageSection>
          <ImageContainer>
            {product.images.map((image, index) => (
              <React.Fragment key={index}>
                <MainImage
                  src={loadedImages.has(index) ? getImageUrl(image, 0) : TRANSPARENT_PIXEL}
                  alt={`${product.name} - Görsel ${index + 1}`}
                  $isActive={index === selectedImage}
                  sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 600px"
                  onClick={() => { 
                    setIsCinemaMode(true); 
                    setIsZoomed(false);
                    setTranslatePos({ x: 0, y: 0 });
                    setLastTranslatePos({ x: 0, y: 0 });
                  }}
                  onError={(e) => handleImageError(index, e)}
                  {...(shouldUseCrossOrigin(image) ? { crossOrigin: "anonymous" } : {})}
                  loading={index === selectedImage ? "eager" : "lazy"}
                  decoding="async"
                  onLoad={() => {
                    setLoadedImages(prev => {
                      if (prev.has(index)) {
                        return prev;
                      }
                      const next = new Set(prev);
                      next.add(index);
                      return next;
                    });
                  }}
                />
                {index === selectedImage && (
                  <ImageBadges>
                    {product.stock === 0 && <Badge type="soldout">Stokta Yok</Badge>}
                    {product.isOnSale && <Badge type="sale">İndirim</Badge>}
                    {product.isFeatured && <Badge type="featured">Öne Çıkan</Badge>}
                    {new Date().getTime() - product.createdAt.getTime() < 7 * 24 * 60 * 60 * 1000 && (
                      <Badge type="new">Yeni</Badge>
                    )}
                  </ImageBadges>
                )}
              </React.Fragment>
            ))}
          </ImageContainer>
        </ImageSection>

        <ProductInfo>
            <ProductTitle>{product.name}</ProductTitle>

            <ProductPrice>
              <CurrentPrice $onSale={product.isOnSale}>
                ₺{product.price.toLocaleString()}
              </CurrentPrice>
              {product.isOnSale && product.originalPrice && (
                <>
                  <OriginalPrice>₺{product.originalPrice.toLocaleString()}</OriginalPrice>
                  <DiscountPercent>%{product.discount}</DiscountPercent>
                </>
              )}
            </ProductPrice>

            {product.sizes.length > 0 && (
              <SizeSelector>
                <SizeLabel>Beden Seçiniz</SizeLabel>
                <SizeOptions>
                  {product.sizes.map((sizeOption) => (
                    <SizeOption
                      key={sizeOption.size}
                      $isSelected={selectedSize === sizeOption.size}
                      onClick={() => setSelectedSize(sizeOption.size)}
                    >
                      {sizeOption.size}
                    </SizeOption>
                  ))}
                </SizeOptions>
              </SizeSelector>
            )}

            <ActionButtons>
              <AddToCartButton onClick={handleAddToCart} disabled={product.stock === 0}>
                <ShoppingCart size={20} />
                {product.stock === 0 ? 'Stokta Yok' : 'Sepete Ekle'}
              </AddToCartButton>
              <WishlistButton onClick={handleWishlist}>
                <Heart size={20} />
              </WishlistButton>
              <ShareButton onClick={handleShare}>
                <Share2 size={20} />
              </ShareButton>
            </ActionButtons>

            <AppointmentButton onClick={() => navigate('/randevu')}>
              <Calendar size={16} />
              Şimdi Randevu Al
            </AppointmentButton>
        </ProductInfo>
      </Container>

      <InfoSection>
        <InfoSectionTitle>{infoSectionTitle}</InfoSectionTitle>
        <InfoSectionRule />

        {(product.detailedDescription || product.shortDescription) && (
          <InfoDescription>
            {product.detailedDescription || product.shortDescription}
          </InfoDescription>
        )}

        {infoRows.length > 0 && (
          <InfoGrid>
            {infoRows.map((row) => (
              <InfoRow key={row.label}>
                <InfoLabel>{row.label}</InfoLabel>
                <InfoValue>{row.value}</InfoValue>
              </InfoRow>
            ))}
          </InfoGrid>
        )}
      </InfoSection>

      {/* Cinema Mode Modal */}
      <CinemaModal $isOpen={isCinemaMode} onClick={() => { 
        setIsCinemaMode(false); 
        setIsZoomed(false); 
        setTranslatePos({ x: 0, y: 0 });
        setLastTranslatePos({ x: 0, y: 0 });
      }}>
        <CloseButton onClick={(e) => { 
          e.stopPropagation();
          setIsCinemaMode(false); 
          setIsZoomed(false); 
          setTranslatePos({ x: 0, y: 0 });
          setLastTranslatePos({ x: 0, y: 0 });
        }}>
          <X size={24} />
        </CloseButton>
        <CinemaImageWrapper>
          <CinemaImage 
            src={getImageUrl(product.images[selectedImage])}
            alt={`${product.name} - Tam Ekran Görsel`}
            $isZoomed={isZoomed}
            $translateX={translatePos.x}
            $translateY={translatePos.y}
            sizes="100vw"
            onClick={handleImageClick}
            onMouseDown={handleImageMouseDown}
            onMouseMove={handleImageMouseMove}
            onMouseUp={handleImageMouseUp}
            onMouseLeave={handleImageMouseUp}
            onError={() => handleImageError(selectedImage)}
            {...(shouldUseCrossOrigin(product.images[selectedImage]) ? { crossOrigin: "anonymous" } : {})}
          />
        </CinemaImageWrapper>
      </CinemaModal>
    </PageContainer>
  );
};

export default ProductDetailPage;
